import React from "react";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { Button } from "../../ui/button";
import { MoreHorizontalIcon } from "lucide-react";
import { cn } from "../../../lib/utils";
import moment from "moment";
import { Tag } from "antd";
import {
  FinetuneRunNew,
  FinetuneRunStatus,
} from "../../../model_gen/finetuning";
import PaginatedTable, {
  PaginatedTableColumn,
  SortConfig,
} from "../../ui/paginated-table";
import { getTimeRemaining } from "./FinetuneRuns";
import {
  handleRetryRun,
  handleDeleteRun,
  handleCancelRun,
  handleRerunFinetune,
} from "./common";

// Simple progress component inline
const Progress: React.FC<{ value: number; className?: string }> = ({
  value,
  className,
}) => (
  <div
    className={`relative h-2 w-full overflow-hidden rounded-full bg-secondary ${
      className || ""
    }`}>
    <div
      className="h-full w-full flex-1 bg-primary transition-all"
      style={{ transform: `translateX(-${100 - value}%)` }}
    />
  </div>
);

interface FinetuneRunsTableProps {
  finetuneRuns: FinetuneRunNew[];
  loading: boolean;
  datasets: { [key: number]: any };
  sortConfig: SortConfig<FinetuneRunNew>;
  onSort: (key: keyof FinetuneRunNew) => void;
  onRunClick: (run: FinetuneRunNew) => void;
  onRefresh: () => void;
  apiKey: ApiKeyOrGetter;
  setIsLoading: (loading: boolean) => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const FinetuneRunsTable: React.FC<FinetuneRunsTableProps> = ({
  finetuneRuns,
  loading,
  datasets,
  sortConfig,
  onSort,
  onRunClick,
  onRefresh,
  apiKey,
  setIsLoading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const { companyId } = usePermissions();

  const isRunFromOtherCompany = (record: FinetuneRunNew): boolean => {
    if (companyId == null) return false;
    return record.company_id !== companyId;
  };

  const getStatusTagColor = (status: FinetuneRunStatus): string => {
    switch (status.toUpperCase()) {
      case "PENDING":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      case "SCHEDULED":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "RUNNING":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "COMPLETED":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "FAILED":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "CANCELLED":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const renderDataSource = (record: FinetuneRunNew) => {
    if (record.finetune_dataset_id) {
      const datasetId = record.finetune_dataset_id;
      const subDatasetId = record.meta?.sub_dataset_id;
      const dataset = datasets[datasetId];
      const subDataset = datasets[subDatasetId];

      if (dataset) {
        let displayName = dataset.name || "Dataset";

        if (subDatasetId && subDataset?.name) {
          displayName += ` / ${subDataset.name}`;
        }

        return (
          <div
            className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors truncate"
            title={displayName}>
            {displayName}
          </div>
        );
      } else {
        return (
          <div className="text-sm font-gc-reg text-foreground truncate">
            Dataset
          </div>
        );
      }
    } else if (record.intent_group_name) {
      return (
        <div
          className="text-sm font-gc-reg text-foreground truncate"
          title={record.intent_group_name}>
          {record.intent_group_name}
        </div>
      );
    } else if (record.application_name && record.intent_name) {
      return (
        <div className="text-sm font-gc-reg text-foreground">
          <div className="truncate" title={record.application_name}>
            {record.application_name}
          </div>
          <div
            className="text-xs text-muted-foreground truncate"
            title={record.intent_name}>
            {record.intent_name}
          </div>
        </div>
      );
    } else {
      return (
        <span className="text-sm text-muted-foreground">
          <Tag color="red">DELETED</Tag>
        </span>
      );
    }
  };

  const renderStatus = (record: FinetuneRunNew) => {
    const statusUpper = record.status.toUpperCase();

    if (statusUpper === "RUNNING") {
      const percent = record.progress ? Math.round(record.progress * 100) : 0;
      return (
        <div className="w-24">
          <Progress value={Number(percent.toFixed(1))} className="h-2" />
          <div className="text-xs text-muted-foreground mt-1">
            {getTimeRemaining(record) || "Processing..."}
          </div>
        </div>
      );
    } else if (statusUpper === "COMPLETED") {
      return (
        <div className="w-24">
          <Progress value={100} className="h-2" />
          <div className="text-xs text-muted-foreground mt-1">Complete</div>
        </div>
      );
    } else {
      return (
        <span
          className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            getStatusTagColor(record.status)
          )}>
          {statusUpper}
        </span>
      );
    }
  };

  const renderAccuracy = (record: FinetuneRunNew) => {
    if (record.validation_accuracy === null) return "-";

    const getScoreColor = (score: number) => {
      if (score >= 80)
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      if (score >= 60)
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400";
      return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
    };

    return (
      <span
        className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getScoreColor(record.validation_accuracy)
        )}>
        {record.validation_accuracy.toFixed(2)}%
      </span>
    );
  };

  const canCancelOrDelete = (status: string) => {
    return [
      "FAILED",
      "COMPLETED",
      "CANCELLED",
      "RUNNING",
      "SCHEDULED",
      "VALIDATING",
      "EXPORTING",
    ].includes(status.toUpperCase());
  };

  const canRetry = (status: string) => {
    return ["FAILED", "COMPLETED", "SCHEDULED", "CANCELLED"].includes(
      status.toUpperCase()
    );
  };

  const isRunningStatus = (status: string) => {
    return ["RUNNING", "SCHEDULED", "VALIDATING", "EXPORTING"].includes(
      status.toUpperCase()
    );
  };

  const renderActionsMenu = (record: FinetuneRunNew) => {
    if (isRunFromOtherCompany(record)) {
      return null;
    }
    return (
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            // Simple dropdown menu using native browser context menu
            const menu = document.createElement("div");
            menu.className =
              "absolute right-0 top-8 z-50 min-w-48 bg-popover border border-border rounded-md shadow-lg py-1";
            menu.innerHTML = `
              <button class="flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                Copy
              </button>
              <button class="flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${
                !canRetry(record.status) ? "opacity-50 cursor-not-allowed" : ""
              }" ${!canRetry(record.status) ? "disabled" : ""}>
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                Retry
              </button>
              <button class="flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-red-600 ${
                !canCancelOrDelete(record.status)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }" ${!canCancelOrDelete(record.status) ? "disabled" : ""}>
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                ${isRunningStatus(record.status) ? "Cancel" : "Delete"}
              </button>
            `;

            // Position and show menu
            const container = e.currentTarget.parentElement;
            if (container) {
              container.appendChild(menu);

              // Add click handlers
              const buttons = menu.querySelectorAll("button");
              buttons[0].onclick = (evt) => {
                evt.stopPropagation();
                handleRerunFinetune(record, datasets, navigate, companyId);
                menu.remove();
              };
              buttons[1].onclick = (evt) => {
                evt.stopPropagation();
                if (canRetry(record.status)) {
                  handleRetryRun(record, setIsLoading, apiKey, onRefresh);
                }
                menu.remove();
              };
              buttons[2].onclick = (evt) => {
                evt.stopPropagation();
                if (canCancelOrDelete(record.status)) {
                  if (isRunningStatus(record.status)) {
                    handleCancelRun(record, setIsLoading, apiKey, onRefresh);
                  } else {
                    handleDeleteRun(record, setIsLoading, apiKey, onRefresh);
                  }
                }
                menu.remove();
              };

              // Remove menu when clicking outside
              const handleClickOutside = (evt: Event) => {
                if (!menu.contains(evt.target as Node)) {
                  menu.remove();
                  document.removeEventListener("click", handleClickOutside);
                }
              };
              setTimeout(
                () => document.addEventListener("click", handleClickOutside),
                0
              );
            }
          }}>
          <MoreHorizontalIcon className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  const columns: PaginatedTableColumn<FinetuneRunNew>[] = [
    {
      key: "display_name",
      header: "Name",
      width: "w-[30%]",
      sortable: true,
      render: (record) => (
        <div
          className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors truncate"
          title={record.display_name}>
          {record.display_name}
        </div>
      ),
    },
    {
      key: "base_model",
      header: "Base Model",
      width: "w-[12%]",
      sortable: true,
      render: (record) => (
        <span
          className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
          )}>
          {record.base_model?.replace("Meta-", "") || "Unknown"}
        </span>
      ),
    },
    {
      key: "meta" as keyof FinetuneRunNew,
      header: "Method",
      width: "w-[7%]",
      render: (record) => {
        const method = record.meta?.training_method || "SFT";
        const badgeColor =
          method === "DPO"
            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
            : method === "GRPO"
            ? "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
            : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
        return (
          <span
            className={cn(
              "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
              badgeColor
            )}>
            {method}
          </span>
        );
      },
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[10%]",
      sortable: true,
      render: (record) =>
        record.date_created ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(record.date_created)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(record.date_created)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        ),
    },
    {
      key: "finetune_dataset_id",
      header: "Data Source",
      width: "w-[20%]",
      render: (record) => renderDataSource(record),
    },
    {
      key: "status",
      header: "Status",
      width: "w-[12%]",
      sortable: true,
      render: (record) => renderStatus(record),
    },
    {
      key: "validation_accuracy",
      header: "Accuracy",
      width: "w-[8%]",
      sortable: true,
      render: (record) => renderAccuracy(record),
    },
    {
      key: "id",
      header: "",
      width: "w-[48px]",
      render: (record) => renderActionsMenu(record),
    },
  ];

  return (
    <PaginatedTable
      data={finetuneRuns}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      rowClassName={(record) =>
        isRunFromOtherCompany(record)
          ? "opacity-60 cursor-default hover:bg-transparent text-muted-foreground"
          : undefined
      }
      getRowProps={(record) =>
        isRunFromOtherCompany(record)
          ? {
              title: record.company_name?.trim()
                ? `Run belongs to ${record.company_name.trim()}`
                : `Run belongs to company ID ${record.company_id}`,
            }
          : {}
      }
      onRowClick={(item) => {
        if (isRunFromOtherCompany(item)) {
          return;
        }
        onRunClick(item);
      }}
      emptyTitle="No finetune runs found"
      emptyDescription="Create a new finetune run to start training your model."
      serverSidePagination={true}
      showPagination={totalPages > 1}
    />
  );
};

export default FinetuneRunsTable;
